import { confirmInGrain } from "../utils/grainDialogs.js";
import React from "react";
import { ActionButton, joinClasses, sectionTagClassName } from "./ResultsPrimitives.jsx";
import {
  isExportApiError,
  loadMeunerieManualTimes,
  saveMeunerieManualTimes,
} from "../lib/exportApi.js";
import { buildMeunerieManualTimeChanges } from "../utils/meunerieManualTimes.js";

const TIME_INPUT_CLASS_NAME = "h-6 w-[6.5rem] rounded-[4px] border border-slate-300 bg-white px-1.5 py-0 text-[0.8rem] font-semibold tabular-nums text-slate-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400";
const SECONDARY_BUTTON_CLASS_NAME = "inline-flex min-h-8 items-center justify-center rounded-[4px] border border-slate-300 bg-white px-3 text-[0.78rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400";
const HEADER_CELL_CLASS_NAME = "border-b border-slate-200 bg-slate-50 px-1.5 py-1 sm:px-2 text-left text-[0.7rem] font-semibold uppercase tracking-[0.06em] text-slate-500";
const CELL_CLASS_NAME = "border-b border-slate-100 px-1.5 py-0.5 sm:px-2 align-middle text-[0.8rem] text-slate-800";

function resolveErrorMessage(error, fallbackMessage) {
  if (isExportApiError(error) || (error instanceof Error && error.message)) {
    return error.message;
  }
  return fallbackMessage;
}

function listRows(documents) {
  return documents.flatMap((reportDocument) => reportDocument.sections.flatMap((section) => section.rows));
}

export default function MeunerieManualTimesModal({
  dispatchDate = "",
  isOpen = false,
  meunerieLabel = "",
  meunerieLookupKey = "",
  onClose,
  onSaved,
}) {
  const [documents, setDocuments] = React.useState([]);
  const [version, setVersion] = React.useState(0);
  const [initialTimes, setInitialTimes] = React.useState({});
  const [draftTimes, setDraftTimes] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [hasConflict, setHasConflict] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setErrorMessage("");
    setHasConflict(false);
    loadMeunerieManualTimes({ dispatchDate, meunerieLookupKey, signal: controller.signal })
      .then((payload) => {
        // Mills without a row that day would only add empty tables.
        const visibleDocuments = payload.documents
          .map((reportDocument) => ({
            ...reportDocument,
            sections: (reportDocument.sections || []).filter((section) => section.rows?.length),
          }))
          .filter((reportDocument) => reportDocument.sections.length > 0);
        const savedTimes = Object.fromEntries(
          listRows(visibleDocuments)
            .filter((row) => row.manual_time_label)
            .map((row) => [row.row_key, row.manual_time_label]),
        );
        setDocuments(visibleDocuments);
        setVersion(payload.version);
        setInitialTimes(savedTimes);
        setDraftTimes(savedTimes);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setDocuments([]);
          setErrorMessage(resolveErrorMessage(error, "Les heures manuelles n'ont pas pu être chargées."));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [dispatchDate, isOpen, meunerieLookupKey, reloadToken]);

  const changes = React.useMemo(() => buildMeunerieManualTimeChanges(initialTimes, draftTimes), [draftTimes, initialTimes]);
  const hasChanges = Object.keys(changes).length > 0;
  const rows = React.useMemo(() => listRows(documents), [documents]);
  const missingCount = rows.filter((row) => !(draftTimes[row.row_key] || "").trim()).length;
  const fillableCount = rows.filter((row) => !(draftTimes[row.row_key] || "").trim() && row.dispatch_time_label).length;

  const requestClose = React.useCallback(async () => {
    if (isSaving) {
      return;
    }
    if (hasChanges && !await confirmInGrain("Les heures modifiées ne seront pas enregistrées.", {
      title: "Abandonner les heures ?", confirmLabel: "Abandonner",
    })) {
      return;
    }
    onClose?.();
  }, [hasChanges, isSaving, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, requestClose]);

  const handleFillFromDispatch = () => {
    setDraftTimes((current) => {
      const next = { ...current };
      rows.forEach((row) => {
        if (!(next[row.row_key] || "").trim() && row.dispatch_time_label) {
          next[row.row_key] = row.dispatch_time_label;
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onClose?.();
      return;
    }

    setIsSaving(true);
    setErrorMessage("");
    try {
      await saveMeunerieManualTimes({ dispatchDate, times: changes, version });
      onSaved?.(`${Object.keys(changes).length} heure(s) manuelle(s) enregistrée(s).`);
      onClose?.();
    } catch (error) {
      setHasConflict(error?.status === 409);
      setErrorMessage(resolveErrorMessage(error, "Les heures manuelles n'ont pas pu être enregistrées."));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-slate-950/45 px-3 py-4 sm:px-4 sm:py-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          requestClose();
        }
      }}
    >
      <section
        aria-label="Heures manuelles du rapport Meunerie"
        aria-modal="true"
        className="flex max-h-full w-full max-w-[min(96vw,64rem)] flex-col rounded-[4px] border border-black/6 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.16)]"
        role="dialog"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="grid gap-0.5">
            <span className={sectionTagClassName}>Rapport Meunerie</span>
            <h3 className="text-[1.05rem] font-semibold tracking-[-0.02em] text-slate-950">Heures manuelles</h3>
            <p className="text-[0.78rem] leading-5 text-slate-600">
              {meunerieLabel || "Meunerie sélectionnée"}
              {dispatchDate ? ` • ${dispatchDate}` : ""}
              {" • Utilisées quand « Heures : Manuelles » est choisi."}
            </p>
          </div>
          <button type="button" className={joinClasses([SECONDARY_BUTTON_CLASS_NAME, "shrink-0 whitespace-nowrap"])} disabled={isSaving} onClick={requestClose}>
            Fermer
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
          {isLoading ? (
            <p className="py-6 text-center text-[0.84rem] text-slate-500">Chargement des lignes du rapport...</p>
          ) : null}

          {!isLoading && !errorMessage && documents.length === 0 ? (
            <p className="rounded-[4px] border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-[0.82rem] text-slate-600">
              Aucune commande assignée pour cette sélection à cette date.
            </p>
          ) : null}

          {!isLoading ? (
            <div className="grid gap-4">
              {documents.map((reportDocument) => (
                <div key={reportDocument.meunerie_label} className="grid gap-2">
                  {documents.length > 1 ? (
                    <h4 className="text-[0.9rem] font-semibold text-slate-950">{reportDocument.meunerie_label}</h4>
                  ) : null}
                  {reportDocument.sections.map((section) => (
                    <div key={section.key} className="overflow-x-auto rounded-[4px] border border-slate-200">
                      <table className="w-full min-w-0 border-collapse">
                        <caption className="bg-white px-2 py-1 text-left text-[0.8rem] font-semibold text-slate-800">
                          {section.title}
                        </caption>
                        <thead>
                          <tr>
                            {/* Same column order as the Meunerie PDF; the dispatch time stays inside the Heure cell for reference. */}
                            <th scope="col" className={HEADER_CELL_CLASS_NAME}>Heure</th>
                            <th scope="col" className={HEADER_CELL_CLASS_NAME}>Bon commande</th>
                            <th scope="col" className={joinClasses([HEADER_CELL_CLASS_NAME, "hidden sm:table-cell"])}>Destinataire</th>
                            <th scope="col" className={HEADER_CELL_CLASS_NAME}>Citerne</th>
                            <th scope="col" className={joinClasses([HEADER_CELL_CLASS_NAME, "hidden md:table-cell"])}>Produit</th>
                            <th scope="col" className={joinClasses([HEADER_CELL_CLASS_NAME, "hidden md:table-cell"])}>Quantité</th>
                            <th scope="col" className={joinClasses([HEADER_CELL_CLASS_NAME, "hidden md:table-cell"])}>Silo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.rows.map((row) => (
                            <tr key={row.row_key}>
                              <td className={joinClasses([CELL_CLASS_NAME, "whitespace-nowrap"])}>
                                <input
                                  type="time"
                                  step={60}
                                  aria-label={`Heure manuelle ${row.order_reference} ${row.truck_name}`}
                                  className={TIME_INPUT_CLASS_NAME}
                                  disabled={isSaving}
                                  value={draftTimes[row.row_key] || ""}
                                  onChange={(event) => {
                                    const { value } = event.target;
                                    setDraftTimes((current) => ({ ...current, [row.row_key]: value }));
                                  }}
                                />
                                <span className="ml-1.5 text-[0.72rem] tabular-nums text-slate-400" title="Heure calculée par le dispatch">
                                  {row.dispatch_time_label ? `Dispatch ${row.dispatch_time_label}` : "Dispatch —"}
                                </span>
                              </td>
                              <td className={joinClasses([CELL_CLASS_NAME, "whitespace-nowrap"])}>{row.order_reference}</td>
                              <td className={joinClasses([CELL_CLASS_NAME, "hidden sm:table-cell"])}>{row.delivery_client_name}</td>
                              <td className={joinClasses([CELL_CLASS_NAME, "whitespace-nowrap"])}>{row.truck_name}</td>
                              <td className={joinClasses([CELL_CLASS_NAME, "hidden md:table-cell"])}>{row.product_code}</td>
                              <td className={joinClasses([CELL_CLASS_NAME, "hidden tabular-nums md:table-cell"])}>{row.quantity_label}</td>
                              <td className={joinClasses([CELL_CLASS_NAME, "hidden md:table-cell"])}>{row.silo_label}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <footer className="grid gap-2 border-t border-slate-200 px-4 py-3">
          {errorMessage ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-[4px] border border-border-danger-subtle bg-danger-soft px-3 py-2 text-[0.82rem] leading-5 text-danger">
              <span>{errorMessage}</span>
              {hasConflict ? (
                <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} onClick={() => setReloadToken((current) => current + 1)}>
                  Recharger
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={SECONDARY_BUTTON_CLASS_NAME}
                disabled={isLoading || isSaving || fillableCount === 0}
                onClick={handleFillFromDispatch}
              >
                Remplir les vides avec le dispatch
              </button>
              {!isLoading && rows.length > 0 ? (
                <span className={joinClasses([
                  "rounded-[4px] border px-2 py-0.5 text-[0.75rem] font-semibold",
                  missingCount > 0 ? "border-border-warning-muted bg-amber-soft text-warning-ink" : "border-slate-200 bg-slate-50 text-slate-600",
                ])}
                >
                  {missingCount > 0 ? `${missingCount} ligne(s) sans heure` : "Toutes les lignes ont une heure"}
                </span>
              ) : null}
            </div>
            <div className="flex gap-2">
              <ActionButton tone="secondary" size="sm" disabled={isSaving} onClick={requestClose}>
                Annuler
              </ActionButton>
              <ActionButton
                tone="primary"
                size="sm"
                disabled={isLoading || isSaving || !hasChanges}
                isLoading={isSaving}
                loadingLabel="Enregistrement..."
                onClick={() => {
                  void handleSave();
                }}
              >
                Enregistrer
              </ActionButton>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
}
